import * as React from 'react';
import Link from 'components/Link';
import { FiMail } from 'react-icons/fi';
import { FaApple, FaFacebookSquare, FaGooglePlay, FaLinkedin, FaTwitter } from 'react-icons/fa';
import {
  ABOUT_PAGE,
  BLOG_INDEX_PAGE,
  CONGRESS_TRADES_PAGE,
  COURSES_INDEX_PAGE,
  DARK_POOL_FLOW_PAGE,
  EARNING_CALENDAR_PAGE_PAGE,
  FINANCE_GLOSSARY_INDEX_PAGE,
  GEX_PAGE,
  HISTORICAL_FLOW_PAGE,
  HOME_PAGE,
  INSIDER_TRADES_PAGE,
  LLMS_FULL_TEXT_PAGE,
  LLMS_TEXT_PAGE,
  LOGIN_PAGE,
  MARKET_HEATMAP_PAGE,
  NANCY_PELOSI_PAGE,
  NEWS_INDEX_PAGE,
  OI_PAGE,
  VOL_PAGE,
  OPC_PAGE,
  OPTION_FLOW_PAGE,
  PRICING_PAGE,
  PRIVACY_PAGE,
  TECHNICAL_ANALYSIS_LANDING_PAGE,
  TERMS_OF_SERVICE_PAGE,
  TICKER_FLOW_PAGE,
  TOP_TICKERS_PAGE,
  WEEKLY_OVERVIEW_PAGE,
} from 'constants/pages';
import { IMPLIED_VOLATILITY_ENABLED } from 'constants/flags';
import LogoImage from 'svg/logo';
import {
  APPLE_APP_STORE_LINK,
  COMPANY_TWITTER,
  COMPANY_FACEBOOK_PAGE,
  COMPANY_LINKEDIN,
  COMPANY_EMAIL,
  GOOGLE_PLAY_STORE_LINK,
} from 'constants/internal';
import { BENZINGA_AWARD_BADGES, BENZINGA_RECOGNITION_TEXT } from 'constants/awards';

import classNames from 'styles/utils/classNames';

import {
  Wrapper,
  GridContent,
  TrustContent,
  LegalContent,
  Disclaimer,
  FooterLogo,
  Copyright,
  LinkGrid,
  LinkGroup,
  LinkGroupTitle,
  LinkColumns,
  LinkList,
  GridLink,
  GridEmailLink,
  BrandRow,
  BrandBlock,
  BrandLine,
  SocialLinks,
  SocialLink,
  BrandLinkRow,
  BrandEmailLink,
  AppLinks,
  AppDownloadLink,
  AppDownloadEyebrow,
  AppDownloadText,
  TrustBlock,
  TrustTitle,
  TrustText,
  AwardBadges,
  AwardBadge,
  AppBlock,
  AccountLinks,
  AccountTextLink,
  BuyNowLink,
  UtilityLinks,
  UtilityLink,
  LegalBlock,
  LegalTopRow,
} from './styles';

interface Props {
  sidebar?: boolean;
  right?: boolean;
  fullwidth?: boolean;
}

interface FooterLinkItem {
  label: string;
  href: string;
  isExternal?: boolean;
  prefetch?: false;
  rel?: React.AnchorHTMLAttributes<HTMLAnchorElement>['rel'];
  target?: React.HTMLAttributeAnchorTarget;
}

/** Renders an EmailLink, which keeps the address out of the server HTML. */
interface FooterEmailItem {
  label: string;
  email: string;
}

type FooterSectionLink = FooterLinkItem | FooterEmailItem;

interface FooterSectionItem {
  title: string;
  links?: FooterSectionLink[];
  linkColumns?: FooterSectionLink[][];
}

const footerSections: FooterSectionItem[] = [
  {
    title: 'Trading Toolkit',
    linkColumns: [
      [
        { label: 'Options Flow', href: OPTION_FLOW_PAGE },
        { label: 'Dark Pool Data', href: DARK_POOL_FLOW_PAGE },
        { label: 'Ticker Research', href: TICKER_FLOW_PAGE },
        { label: 'Top Tickers', href: TOP_TICKERS_PAGE },
        { label: 'Historical Flow', href: HISTORICAL_FLOW_PAGE },
        { label: 'Weekly Overview', href: WEEKLY_OVERVIEW_PAGE },
      ],
      [
        { label: 'Technical Analysis', href: TECHNICAL_ANALYSIS_LANDING_PAGE },
        { label: 'Options Profit Calculator', href: OPC_PAGE },
        { label: 'Gamma Exposure', href: GEX_PAGE },
        { label: 'Open Interest Levels', href: OI_PAGE },
        ...(IMPLIED_VOLATILITY_ENABLED ? [{ label: 'Implied Volatility', href: VOL_PAGE }] : []),
        { label: 'S&P 500 Heatmap', href: MARKET_HEATMAP_PAGE },
      ],
      [
        { label: 'Earnings Calendar', href: EARNING_CALENDAR_PAGE_PAGE },
        { label: 'Insider Trades', href: INSIDER_TRADES_PAGE },
        { label: 'Nancy Pelosi Stock Trades', href: NANCY_PELOSI_PAGE },
        { label: 'Congress Trades', href: CONGRESS_TRADES_PAGE },
        { label: 'Market News', href: NEWS_INDEX_PAGE },
      ],
    ],
  },
  {
    title: 'Learn',
    links: [
      { label: 'Trading Courses', href: COURSES_INDEX_PAGE },
      { label: 'Resource Center', href: BLOG_INDEX_PAGE },
      { label: 'Finance Glossary', href: FINANCE_GLOSSARY_INDEX_PAGE },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'Pricing', href: PRICING_PAGE },
      { label: 'About InsiderFinance', href: ABOUT_PAGE },
      { label: 'Contact Support', email: COMPANY_EMAIL },
    ],
  },
];

const THIRD_PARTY_LINK_REL = 'noopener noreferrer nofollow';

function FooterGridLink({ link }: { link: FooterSectionLink }) {
  if ('email' in link) {
    return (
      <GridEmailLink email={link.email} target="_blank" rel="noopener noreferrer">
        {link.label}
      </GridEmailLink>
    );
  }
  return (
    <GridLink href={link.href} isExternal={link.isExternal} rel={link.rel}>
      {link.label}
    </GridLink>
  );
}

const utilityLinks: FooterLinkItem[] = [
  { label: 'Terms of Service', href: TERMS_OF_SERVICE_PAGE },
  { label: 'Privacy Policy', href: PRIVACY_PAGE },
  { label: 'AI summary', href: LLMS_TEXT_PAGE, isExternal: true, target: '_self' },
  { label: 'Full AI-readable docs', href: LLMS_FULL_TEXT_PAGE, isExternal: true, target: '_self' },
];

const Footer: React.FC<React.PropsWithChildren<Props>> = ({
  sidebar,
  right,
  fullwidth = false,
}) => {
  return (
    <Wrapper sidebar={sidebar} right={right} className="styled-font">
      <BrandRow className={classNames(fullwidth ? '!max-w-[100%]' : '')}>
        <BrandBlock>
          <FooterLogo>
            <Link href={HOME_PAGE}>
              <LogoImage />
            </Link>
          </FooterLogo>
          <BrandLine>
            InsiderFinance is a financial intelligence and trading analytics platform for active
            traders.
          </BrandLine>
          <BrandLinkRow>
            <SocialLinks>
              <SocialLink href={COMPANY_FACEBOOK_PAGE} isExternal rel={THIRD_PARTY_LINK_REL}>
                <span className="sr-only">Facebook</span>
                <FaFacebookSquare />
              </SocialLink>
              <SocialLink href={COMPANY_TWITTER} isExternal rel={THIRD_PARTY_LINK_REL}>
                <span className="sr-only">X / Twitter</span>
                <FaTwitter />
              </SocialLink>
              <SocialLink href={COMPANY_LINKEDIN} isExternal rel={THIRD_PARTY_LINK_REL}>
                <span className="sr-only">LinkedIn</span>
                <FaLinkedin />
              </SocialLink>
            </SocialLinks>
            <BrandEmailLink
              email={COMPANY_EMAIL}
              icon={<FiMail />}
              target="_blank"
              rel="noopener noreferrer"
            />
          </BrandLinkRow>
        </BrandBlock>
        <AccountLinks>
          <AccountTextLink href={LOGIN_PAGE}>Log in</AccountTextLink>
          <BuyNowLink href={PRICING_PAGE}>Buy Now</BuyNowLink>
        </AccountLinks>
      </BrandRow>
      <GridContent className={classNames(fullwidth ? '!max-w-[100%]' : '')}>
        <LinkGrid>
          {footerSections.map((section) => (
            <LinkGroup key={section.title}>
              <LinkGroupTitle>{section.title}</LinkGroupTitle>
              {section.linkColumns ? (
                <LinkColumns>
                  {section.linkColumns.map((column, columnIndex) => (
                    <LinkList key={`${section.title}-${columnIndex}`}>
                      {column.map((link) => (
                        <li key={`${section.title}-${link.label}`}>
                          <FooterGridLink link={link} />
                        </li>
                      ))}
                    </LinkList>
                  ))}
                </LinkColumns>
              ) : (
                <LinkList>
                  {section.links?.map((link) => (
                    <li key={`${section.title}-${link.label}`}>
                      <FooterGridLink link={link} />
                    </li>
                  ))}
                </LinkList>
              )}
            </LinkGroup>
          ))}
        </LinkGrid>
      </GridContent>
      <TrustContent className={classNames(fullwidth ? '!max-w-[100%]' : '')}>
        <TrustBlock>
          <div>
            <TrustTitle>Recognition</TrustTitle>
            <TrustText>{BENZINGA_RECOGNITION_TEXT}</TrustText>
          </div>
          <AwardBadges>
            {BENZINGA_AWARD_BADGES.map((badge) => (
              <AwardBadge key={badge.src} src={badge.src} alt={badge.alt} loading="lazy" />
            ))}
          </AwardBadges>
        </TrustBlock>
        <AppBlock>
          <TrustTitle>GET THE INSIDERFINANCE MOBILE APPS</TrustTitle>
          <AppLinks>
            <AppDownloadLink
              href={APPLE_APP_STORE_LINK}
              isExternal
              target="_blank"
              rel={THIRD_PARTY_LINK_REL}
            >
              <FaApple />
              <span>
                <AppDownloadEyebrow>Download on the</AppDownloadEyebrow>
                <AppDownloadText>App Store</AppDownloadText>
              </span>
            </AppDownloadLink>
            <AppDownloadLink
              href={GOOGLE_PLAY_STORE_LINK}
              isExternal
              target="_blank"
              rel={THIRD_PARTY_LINK_REL}
            >
              <FaGooglePlay />
              <span>
                <AppDownloadEyebrow>Get it on</AppDownloadEyebrow>
                <AppDownloadText>Google Play</AppDownloadText>
              </span>
            </AppDownloadLink>
          </AppLinks>
        </AppBlock>
      </TrustContent>
      <LegalContent className={classNames('flex-col', fullwidth ? '!max-w-[100%]' : '')}>
        <LegalBlock>
          <LegalTopRow>
            {/* The page may be built in one year and hydrated in the next (UTC server, or no
                rebuild since December). React then updates the year instead of failing. */}
            <Copyright suppressHydrationWarning>
              InsiderFinance Intelligence LLC © {new Date().getFullYear()}
            </Copyright>
            <UtilityLinks>
              {utilityLinks.map((link) => (
                <UtilityLink
                  key={link.href}
                  href={link.href}
                  isExternal={link.isExternal}
                  prefetch={link.prefetch}
                  rel={link.rel}
                  target={link.target}
                >
                  {link.label}
                </UtilityLink>
              ))}
            </UtilityLinks>
          </LegalTopRow>
          <Disclaimer>
            <strong>Legal Disclaimer:</strong> Insider Finance Intelligence LLC (InsiderFinance) is
            not a registered investment advisor nor is licensed as such with any federal or state
            regulatory agency. InsiderFinance does not manage client assets in any way. Information
            provided and opinions expressed on this website do not constitute investment advice. The
            ideas expressed on this site and related services are solely the opinions of
            InsiderFinance and are for educational purposes only. We advise everyone to know the
            risks involved with trading stocks and options. We encourage every visitor to the
            website to do his/her own research and to consult with his/her own financial advisor(s)
            prior to engaging in any investing activities, and to make sure he/she knows all the
            potential risks involved. Any investment decision that results in losses or gains made
            based on any information on this site or related services is not the responsibility of
            InsiderFinance. InsiderFinance is solely a data-provider and not a stock picks alert
            service.
          </Disclaimer>
        </LegalBlock>
      </LegalContent>
    </Wrapper>
  );
};

export default Footer;
